import React, { useState, useEffect, useRef } from "react";
import {
  Play,
  Code,
  MessageSquare,
  Target,
  Video,
  BarChart,
  Book,
  CreditCard,
  Star,
  ChevronRight,
  Sparkles,
  Zap,
  Award,
  TrendingUp,
  Orbit,
  Atom,
  Gem,
  Crown,
  Infinity,
  Shield,
  Rocket,
} from "lucide-react";

const CourseHighlights = () => {
  /* ────────────────────────────────────────────────────────────
     State + refs
  ──────────────────────────────────────────────────────────── */
  const [hoveredCard, setHoveredCard] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const containerRef = useRef(null);

  /* smooth mouse-tracking for subtle parallax effects  */
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      setMousePosition({
        x: (e.clientX - rect.left) / rect.width,
        y: (e.clientY - rect.top) / rect.height,
      });
    };
    const node = containerRef.current;
    node?.addEventListener("mousemove", handleMouseMove);
    return () => node?.removeEventListener("mousemove", handleMouseMove);
  }, []);

  /* ────────────────────────────────────────────────────────────
     Highlight-card data
  ──────────────────────────────────────────────────────────── */
  const highlights = [
    {
      id: 1,
      icon: Play,
      number: "115+",
      title: "Live Interactive Sessions",
      subtitle: "Real-time Learning Experience",
      gradient: "linear-gradient(135deg,#3b82f6,#1e40af,#1e3a8a)",
      particleColor: "#3b82f6",
      backContent:
        "Experience immersive real-time coding sessions with industry experts. Interactive Q&A, live debugging challenges, collaborative coding, and hands-on practice with immediate mentor feedback.",
      features: [
        "Live coding sessions",
        "Real-time Q&A",
        "Expert mentorship",
        "Interactive labs",
        "Peer collaboration",
        "Instant feedback",
      ],
    },
    {
      id: 2,
      icon: Code,
      number: "240+",
      title: "Assignments & Real-World Projects",
      subtitle: "Portfolio Building Experience",
      gradient: "linear-gradient(135deg,#10b981,#059669,#047857)",
      particleColor: "#10b981",
      backContent:
        "Build production-ready projects using real industry datasets. Complete end-to-end ML pipelines from data ingestion, cleaning, feature engineering to model deployment and monitoring.",
      features: [
        "Portfolio projects",
        "Industry datasets",
        "End-to-end ML",
        "Production deployment",
        "Code reviews",
        "Best practices",
      ],
    },
    {
      id: 3,
      icon: MessageSquare,
      number: "24/7",
      title: "Doubt Clearing & Mentorship",
      subtitle: "Personal Guidance System",
      gradient: "linear-gradient(135deg,#8b5cf6,#7c3aed,#6d28d9)",
      particleColor: "#8b5cf6",
      backContent:
        "Get personalized guidance from senior data scientists and ML engineers. Weekly 1:1 sessions, career roadmaps, technical mentorship, and round-the-clock doubt-resolution support.",
      features: [
        "24/7 support",
        "1:1 mentoring",
        "Career guidance",
        "Expert network",
        "Technical reviews",
        "Skill assessment",
      ],
    },
    {
      id: 4,
      icon: Target,
      number: "100%",
      title: "Job Referrals & Placement Support",
      subtitle: "Career Success Guarantee",
      gradient: "linear-gradient(135deg,#6366f1,#3b82f6,#06b6d4)",
      particleColor: "#6366f1",
      isLarge: true, // this card spans two columns on desktop
      backContent:
        "Direct referrals to FAANG companies and top unicorn startups. Comprehensive interview preparation, resume optimisation, mock interviews, salary-negotiation training, and guaranteed placement support until you land your dream role.",
      features: [
        "FAANG referrals",
        "Mock interviews",
        "Resume optimisation",
        "Salary negotiation",
        "Placement guarantee",
        "Career coaching",
        "Interview prep",
        "Network access",
      ],
    },
    {
      id: 5,
      icon: Video,
      number: "∞",
      title: "Lifetime Access to Resources",
      subtitle: "Learn at Your Own Pace",
      gradient: "linear-gradient(135deg,#f97316,#dc2626,#be185d)",
      particleColor: "#f97316",
      backContent:
        "Lifetime access to all course materials, HD recordings, future content updates, exclusive masterclasses, and new-tech additions. Learn and revisit concepts anytime, anywhere.",
      features: [
        "Lifetime access",
        "4K recordings",
        "Future updates",
        "Mobile app",
        "Offline access",
        "New content",
      ],
    },
    {
      id: 6,
      icon: BarChart,
      number: "60+",
      title: "Practice & Aptitude Tests",
      subtitle: "Skill Assessment Platform",
      gradient: "linear-gradient(135deg,#06b6d4,#0891b2,#0e7490)",
      particleColor: "#06b6d4",
      backContent:
        "Advanced skill assessments with AI-powered feedback. Industry-standard coding tests, statistical-analysis challenges, ML algorithm implementations, and detailed performance analytics.",
      features: [
        "AI feedback",
        "Skill tracking",
        "Coding challenges",
        "Performance metrics",
        "Progress analytics",
        "Weakness analysis",
      ],
    },
    {
      id: 7,
      icon: Book,
      number: "460+",
      title: "Self-Paced Learning Resources",
      subtitle: "Comprehensive Learning Library",
      gradient: "linear-gradient(135deg,#ec4899,#db2777,#be185d)",
      particleColor: "#ec4899",
      backContent:
        "Extensive learning library with interactive tutorials, real-world case studies, cutting-edge research papers, curated datasets, and hands-on exercises for comprehensive skill development.",
      features: [
        "Interactive tutorials",
        "Case studies",
        "Research papers",
        "Practice datasets",
        "Code repositories",
        "Video content",
      ],
    },
    {
      id: 8,
      icon: CreditCard,
      number: "0%",
      title: "No-Cost EMI Options",
      subtitle: "Flexible Payment Solutions",
      gradient: "linear-gradient(135deg,#10b981,#059669,#65a30d)",
      particleColor: "#10b981",
      backContent:
        "Flexible payment solutions with 0%-interest EMI options. Multiple tenure choices, instant approval, and completely transparent pricing with no hidden charges.",
      features: [
        "0% interest",
        "Instant approval",
        "Flexible tenure",
        "Transparent pricing",
        "No hidden fees",
        "Easy processing",
      ],
    },
  ];

  /* ────────────────────────────────────────────────────────────
     Helper renderers – tiny utility div generators
  ──────────────────────────────────────────────────────────── */
  const createParticles = (count, color) =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="particle"
        style={{
          left: `${15 + ((i * 10) % 70)}%`,
          top: `${10 + ((i * 8) % 80)}%`,
          background: color,
          animationDelay: `${i * 0.2}s`,
          animationDuration: `${3 + (i % 3)}s`,
        }}
      />
    ));

  const createStarField = (count) =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="star1"
        style={{
          left: `${Math.random() * 100}%`,
          top: `${Math.random() * 100}%`,
          animationDelay: `${i * 0.3}s`,
        }}
      />
    ));

  const createNeuralLines = (count) =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="neural-line"
        style={{
          left: `${20 + i * 15}%`,
          top: `${30 + i * 8}%`,
          transform: `rotate(${i * 30}deg)`,
          animationDelay: `${i * 0.2}s`,
        }}
      />
    ));

  /* ────────────────────────────────────────────────────────────
     Render
  ──────────────────────────────────────────────────────────── */
  return (
    <div ref={containerRef} className="course-highlights-container">
      {/* ———  Ornamental background system  ——— */}
      <div className="background-system">
        <div className="animated-grid">
          {Array.from({ length: 30 }).map((_, i) => (
            <div
              key={`v-${i}`}
              className="grid-line-vertical"
              style={{ left: `${i * 3.33}%`, animationDelay: `${i * 0.1}s` }}
            />
          ))}
          {Array.from({ length: 20 }).map((_, i) => (
            <div
              key={`h-${i}`}
              className="grid-line-horizontal"
              style={{ top: `${i * 5}%`, animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>

        <div className="cosmic-elements">
          <div className="cosmic-orb cosmic-orb-1" />
          <div className="cosmic-orb cosmic-orb-2" />
          <div className="cosmic-orb cosmic-orb-3" />
          <div className="cosmic-orb cosmic-orb-4" />
          <div className="orbital-ring orbital-ring-1" />
          <div className="orbital-ring orbital-ring-2" />
        </div>

        <div className="light-sweep" />
      </div>

      {/* ———  TEXT header  ——— */}
      <div className="content-container">
        <div className="header-section">
          <div className="header-badge">
            <Crown className="crown-icon" />
            <span className="header-badge-text">
              Premium Data Science & AI Course
            </span>
            <Sparkles className="sparkles-icon" />
          </div>

          <h2 className="main-title">1. Course Highlights</h2>

          <p className="subtitle">
            Discover what makes our <a href="https://logicmojo.com/data-science-roadmap" className="feature-link">Data Science</a> course extraordinary with
            cutting-edge learning paths, expert mentorship, and guaranteed career
            transformation.
          </p>

          <div  className="title-underline" />

          {/* floating SVG decorations */}
          <Atom className="floating-decoration atom-decoration" />
          <Orbit className="floating-decoration orbit-decoration" />
          <Rocket className="floating-decoration rocket-decoration" />
        </div>

        {/* ———  GRID of flipping cards  ——— */}
        <div className="highlights-grid">
          {highlights.map((item, index) => {
            const Icon = item.icon;
            const isHovered = hoveredCard === item.id;

            return (
              <div
                key={item.id}
                className={`highlight-card ${item.isLarge ? "large" : ""}`}
                onMouseEnter={() => setHoveredCard(item.id)}
                onMouseLeave={() => setHoveredCard(null)}
                style={{ animationDelay: `${index * 250}ms` }}
              >
                {/*  flip container */}
                <div className={`card-inner ${isHovered ? "flipped" : ""}`}>
                  {/* ── FRONT  ────────────────── */}
                  <div className="card-front">
                    <div className="card-background-layers">
                      <div
                        className="background-gradient"
                        style={{ background: item.gradient }}
                      />
                      <div className="particle-field">
                        {createParticles(10, item.particleColor)}
                      </div>
                      <div className="geometric-overlay">
                        <div
                          className="geometric-shape geometric-shape-1"
                          style={{ background: item.gradient }}
                        />
                        <div
                          className="geometric-shape geometric-shape-2"
                          style={{ background: item.gradient }}
                        />
                        <div
                          className="geometric-shape geometric-shape-3"
                          style={{ background: item.gradient }}
                        />
                      </div>
                      <div className="neural-pattern">
                        {createNeuralLines(6)}
                      </div>
                    </div>

                    <div className="card-content">
                      {/* header */}
                      <div className="card-header">
                        <div
                          className="icon-container"
                          style={{ background: item.gradient }}
                        >
                          <Icon className="card-icon" />
                          <div
                            className="icon-glow"
                            style={{ background: item.gradient }}
                          />
                          <div className="icon-depth" />
                          <div className="icon-shadow" />
                        </div>

                        <div className="floating-decorations">
                          <div className="floating-dot floating-dot-1" />
                          <div className="floating-dot floating-dot-2" />
                          <div className="floating-dot floating-dot-3" />
                        </div>

                        <div className="number-section">
                          <div
                            className="card-number"
                            style={{
                              background: item.gradient,
                              backgroundClip: "text",
                              WebkitBackgroundClip: "text",
                            }}
                          >
                            {item.number}
                          </div>
                          <div className="number-decorations">
                            <Sparkles className="number-decoration sparkles-decoration" />
                            <Gem className="number-decoration gem-decoration" />
                            <Zap className="number-decoration zap-decoration" />
                          </div>
                        </div>
                      </div>

                      {/* content */}
                      <div className="content-section">
                        <h3 className="card-title">{item.title}</h3>
                        <p className="card-subtitle">{item.subtitle}</p>
                      </div>

                      {/* footer */}
                      <div className="card-footer">
                        <div className="footer-row">
                          <div className="star-rating">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <Star
                                key={star}
                                className="star"
                                style={{ animationDelay: `${star * 100}ms` }}
                              />
                            ))}
                          </div>
                          <div className="footer-actions">
                            <span className="explore-text">Explore</span>
                            <ChevronRight className="chevron-icon" />
                            <TrendingUp className="trend-icon" />
                          </div>
                        </div>

                        <div className="progress-container">
                          <div
                            className="progress-bar"
                            style={{ background: item.gradient }}
                          >
                            <div className="progress-shimmer" />
                            <div className="progress-indicator" />
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="corner-decorations corner-decoration-bottom">
                      <Shield className="corner-icon shield-icon" />
                    </div>
                    <div className="corner-decorations corner-decoration-top">
                      <Infinity className="corner-icon infinity-icon" />
                    </div>
                  </div>

                  {/* ── BACK  ────────────────── */}
                  <div
                    className="card-back"
                    style={{ background: item.gradient }}
                  >
                    <div className="back-cosmic-background">
                      <div className="cosmic-orbit-1" />
                      <div className="cosmic-orbit-2" />
                      <div className="cosmic-center" />
                      <div className="star-field">
                        {createStarField(15)}
                      </div>
                    </div>

                    <div className="back-content">
                      <div className="back-header">
                        <Award className="award-icon" />
                        <div className="back-title-section">
                          <h3>Premium Details</h3>
                          <p>Comprehensive Overview</p>
                        </div>
                      </div>

                      <p
                        className={`back-description ${
                          item.isLarge ? "large" : "small"
                        }`}
                      >
                        {item.backContent}
                      </p>

                      <div className="features-grid">
                        {item.features.map((feature, idx) => (
                          <div
                            key={idx}
                            className="feature-item1"
                            style={{ animationDelay: `${idx * 0.1}s` }}
                          >
                            <div className="feature-dot" />
                            <span className="feature-text1">{feature}</span>
                          </div>
                        ))}
                      </div>

                      <div className="back-footer">
                        <div className="back-button">
                          <div className="back-button-content">
                            <ChevronRight className="back-chevron" />
                            <span>Hover to return</span>
                            <Sparkles className="back-sparkles" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      
      </div>
    </div>
  );
};

export default CourseHighlights;
